import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

/**
 * Subtle YouTube-powered background music.
 *
 * Browsers block audio autoplay-with-sound, but they DO allow muted autoplay.
 * So we:
 *   1. Load the YouTube IFrame muted, autoplay=1, looping a 2-song playlist.
 *   2. The moment the user clicks the age-gate confirm (a real user gesture),
 *      we call unMute() + setVolume(40) — sound starts seamlessly.
 *
 * Exposes a context with `setMuted(bool)` so the age gate can unmute it.
 */

const TRACKS = [
  "bz4zSaiBEG4", // Britney Spears — Bad Girl
  "tTxVmao5log", // Britney Spears — Breathe On Me
];
const TARGET_VOLUME = 40; // 0-100

const MusicContext = createContext({
  ready: false,
  muted: true,
  closed: false,
  setMuted: () => {},
  close: () => {},
});

export const useMusic = () => useContext(MusicContext);

export function MusicProvider({ children }) {
  const playerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [muted, setMutedState] = useState(true);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("luxe_music_closed") === "1") setClosed(true);
  }, []);

  // Load the YouTube IFrame API once
  useEffect(() => {
    if (closed) return;
    if (!window.YT) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      tag.async = true;
      document.body.appendChild(tag);
    }

    const init = () => {
      if (playerRef.current) return;
      playerRef.current = new window.YT.Player("luxe-yt-player", {
        height: "1",
        width: "1",
        videoId: TRACKS[0],
        playerVars: {
          autoplay: 1,
          controls: 0,
          loop: 1,
          playlist: TRACKS.join(","),
          mute: 1,
          playsinline: 1,
          modestbranding: 1,
          rel: 0,
          iv_load_policy: 3,
          fs: 0,
          disablekb: 1,
        },
        events: {
          onReady: (e) => {
            try {
              e.target.mute();
              e.target.setVolume(TARGET_VOLUME);
              e.target.playVideo();
            } catch {}
            setReady(true);
          },
          onStateChange: (e) => {
            // Auto-advance to next track when one ends; loop=1 handles the playlist loop
            if (e.data === window.YT.PlayerState.ENDED) {
              try { e.target.playVideo(); } catch {}
            }
          },
        },
      });
    };

    if (window.YT && window.YT.Player) {
      init();
    } else {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof prev === "function") prev();
        init();
      };
    }

    return () => {
      try {
        playerRef.current?.destroy?.();
      } catch {}
      playerRef.current = null;
    };
  }, [closed]);

  const setMuted = useCallback((next) => {
    setMutedState(next);
    const p = playerRef.current;
    if (!p) return;
    try {
      if (next) {
        p.mute();
      } else {
        p.unMute();
        p.setVolume(TARGET_VOLUME);
        p.playVideo();
      }
    } catch {}
  }, []);

  // Returning visitors skip the age gate — so we attach a one-shot listener
  // for ANY user gesture (click, tap, keypress, scroll) and unmute the moment
  // the browser allows it. Net effect: music starts the instant the visitor
  // touches the page. This is the closest legal substitute for "instant audio".
  useEffect(() => {
    if (closed) return;
    if (!muted) return;
    const ageVerified =
      typeof window !== "undefined" &&
      localStorage.getItem("luxe_age_verified_v1") === "1";
    if (!ageVerified) return; // age gate will handle the unmute on first visit

    const unlock = () => {
      setMuted(false);
      cleanup();
    };
    const cleanup = () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("scroll", unlock);
      window.removeEventListener("mousemove", unlock);
    };
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("touchstart", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    window.addEventListener("scroll", unlock, { once: true, passive: true });
    window.addEventListener("mousemove", unlock, { once: true });
    return cleanup;
  }, [closed, muted, setMuted]);

  const close = useCallback(() => {
    setClosed(true);
    sessionStorage.setItem("luxe_music_closed", "1");
    try {
      playerRef.current?.stopVideo?.();
      playerRef.current?.destroy?.();
    } catch {}
    playerRef.current = null;
  }, []);

  return (
    <MusicContext.Provider value={{ ready, muted, closed, setMuted, close }}>
      {children}
      {/* Hidden YouTube IFrame — must remain mounted to keep audio alive */}
      {!closed && (
        <div
          aria-hidden
          style={{
            position: "fixed",
            width: 1,
            height: 1,
            opacity: 0,
            pointerEvents: "none",
            left: 0,
            top: 0,
            overflow: "hidden",
          }}
        >
          <div id="luxe-yt-player" />
        </div>
      )}
    </MusicContext.Provider>
  );
}
